import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import CollectionsAPI from 'services/API/CollectionsAPI';
import { PaginatedProject } from 'types';
import { CategoryEnum } from 'types/SavedProject';

type TPaginatedSavedProjects = {
  time: number;
  count: number;
  results: PaginatedProject[];
};

type TSummaryObject = {
  number_of_watch_projects: number;
  number_of_star_projects: number;
};

type TPaginatedProps = {
  pageNumber?: number;
  sort?: string[];
  category: CategoryEnum;
};

export const useDefaultCollections = () => {
  const queryClient = useQueryClient();

  const invalidateGetNumSavedProjectsByCategory = useCallback(() => {
    queryClient.invalidateQueries(['default-collection-summary']);
  }, [queryClient]);

  const getSavedProjectsByCategoryQueryKey = ['default-collection-summary'];

  const getSavedProjectsByCategory = useQuery(
    getSavedProjectsByCategoryQueryKey,
    async (): Promise<TSummaryObject> => {
      const result = await CollectionsAPI.getDefaultCollectionSummary();

      return (
        result?.data?.data || {
          number_of_watch_projects: 0,
          number_of_star_projects: 0,
        }
      );
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const getPaginatedSavedProjectsByCategory = ({
    pageNumber = 1,
    category,
    sort = [],
  }: TPaginatedProps) =>
    useQuery(
      ['default-collections-project', { sort, pageNumber, category }],
      async (): Promise<TPaginatedSavedProjects | null> => {
        const result = await CollectionsAPI.postDefaultCollectionProjects(
          pageNumber,
          category,
          sort,
        );

        return result?.data || null;
      },
      {
        refetchOnWindowFocus: false,
      },
    );

  return {
    getSavedProjectsByCategory,
    invalidateGetNumSavedProjectsByCategory,
    getPaginatedSavedProjectsByCategory,
  };
};
